/**
 * Venue / destination control per service (M5, R18 venue memory):
 *  - wedding: native select over the curated /api/venues list + past venues
 *    (/api/venue-suggestions) appended, with an 'Other / not listed' reveal.
 *  - private-tour: free text 'Destination' + a suggestions datalist.
 * The venue lives in the STORE from ANY input path — a prefilled venue
 * renders selected here identically to a typed one (the layer-1 fix by
 * construction). Operator-authored strings render as text/options only.
 */
import { useEffect, useState } from 'preact/hooks';
import { getOr } from '../lib/api.js';

const OTHER = '__other__';

export type VenueControlProps = {
  service: string;
  venue: string | null;
  onVenue: (venue: string | null) => void;
};

export function VenueControl({ service, venue, onVenue }: VenueControlProps) {
  const [curated, setCurated] = useState<string[]>([]);
  const [past, setPast] = useState<string[]>([]);

  useEffect(() => {
    if (service === 'wedding') {
      void getOr<{ venues: string[] }>('/api/venues', { venues: [] }).then((d) => setCurated(d.venues));
    }
    void getOr<{ suggestions: string[] }>(
      `/api/venue-suggestions?service=${encodeURIComponent(service)}`,
      { suggestions: [] },
    ).then((d) => setPast(d.suggestions));
  }, [service]);

  if (service === 'wedding') {
    const known = [...curated, ...past.filter((p) => !curated.includes(p))];
    return <WeddingSelect known={known} venue={venue} onVenue={onVenue} />;
  }

  // private-tour: free-text destination with a past-suggestions datalist.
  return (
    <div class="venue-control">
      <label class="field-label" for="venue-input">Destination</label>
      <input
        id="venue-input"
        class="login-input"
        type="text"
        list="venue-suggestions"
        value={venue ?? ''}
        onInput={(e) => onVenue((e.target as HTMLInputElement).value || null)}
      />
      <datalist id="venue-suggestions">
        {past.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>
    </div>
  );
}

function WeddingSelect(props: {
  known: string[];
  venue: string | null;
  onVenue: (venue: string | null) => void;
}) {
  // The 'Other' reveal is DERIVED, never snapshotted: the venue lists load
  // async, so state initialised before they arrive goes stale (M5 gate BLOCK
  // — a prefilled KNOWN venue rendered as Other). `otherForced` records only
  // an explicit operator choice; everything else follows the store venue.
  const [otherForced, setOtherForced] = useState(false);
  const isKnown = props.venue !== null && props.known.includes(props.venue);
  const showOther = otherForced || (props.venue !== null && props.venue !== '' && !isKnown);
  const selectValue = showOther ? OTHER : props.venue ?? '';

  return (
    <div class="venue-control">
      <label class="field-label" for="venue-select">Venue</label>
      <select
        id="venue-select"
        class="login-input venue-select"
        value={selectValue}
        onChange={(e) => {
          const v = (e.target as HTMLSelectElement).value;
          if (v === OTHER) {
            setOtherForced(true);
            props.onVenue(null);
          } else {
            setOtherForced(false);
            props.onVenue(v || null);
          }
        }}
      >
        <option value="">Choose a venue…</option>
        {props.known.map((v) => (
          <option key={v} value={v}>{v}</option>
        ))}
        <option value={OTHER}>Other / not listed</option>
      </select>
      {showOther && (
        <>
          <label class="field-label" for="venue-other">Venue name</label>
          <input
            id="venue-other"
            class="login-input"
            type="text"
            value={isKnown ? '' : props.venue ?? ''}
            onInput={(e) => props.onVenue((e.target as HTMLInputElement).value || null)}
          />
        </>
      )}
    </div>
  );
}
